import { useState } from "react";
import axios from "axios";
import {
  Copy,
  Gift,
  Loader2,
  Mail,
  Play,
  Send,
} from "lucide-react";
import { toast } from "sonner";

import { API, authHeaders } from "../context/AuthContext";

export default function AdminMarketingActions({ onRefresh }) {
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);

  const [testEmail, setTestEmail] = useState("");
  const [generatedCodes, setGeneratedCodes] = useState([]);

  const [bulk, setBulk] = useState({
    prefix: "PROMO",
    count: 10,
    percent_off: 20,
    valid_days: 30,
    max_uses: 1,
    package_id: "",
    description: "",
  });

  const sendNewsletterNow = async () => {
    if (
      !window.confirm(
        "Uruchomić teraz automatyczną wysyłkę newslettera?"
      )
    ) {
      return;
    }

    setNewsletterLoading(true);

    try {
      const response = await axios.post(
        `${API}/admin/marketing/newsletter/send-now`,
        {},
        { headers: authHeaders() }
      );

      toast.success("Newsletter został uruchomiony");

      console.log("Newsletter result:", response.data);

      if (onRefresh) {
        await onRefresh();
      }
    } catch (error) {
      toast.error(
        error.response?.data?.detail ||
          "Nie udało się uruchomić newslettera"
      );
    } finally {
      setNewsletterLoading(false);
    }
  };

  const sendTestEmail = async () => {
    const email = testEmail.trim();

    if (!email) {
      toast.error("Wpisz adres e-mail");
      return;
    }

    setTestLoading(true);

    try {
      await axios.post(
        `${API}/admin/marketing/newsletter/send-test`,
        {
          email,
          lang: "pl",
        },
        { headers: authHeaders() }
      );

      toast.success(`Wysłano wiadomość testową do ${email}`);

      if (onRefresh) {
        await onRefresh();
      }
    } catch (error) {
      toast.error(
        error.response?.data?.detail ||
          "Nie udało się wysłać wiadomości testowej"
      );
    } finally {
      setTestLoading(false);
    }
  };

  const generateBulkCoupons = async () => {
    if (!bulk.prefix.trim()) {
      toast.error("Wpisz prefiks kodów");
      return;
    }

    if (
      !window.confirm(
        `Wygenerować ${bulk.count} kodów rabatowych -${bulk.percent_off}%?`
      )
    ) {
      return;
    }

    setBulkLoading(true);

    try {
      const response = await axios.post(
        `${API}/admin/marketing/coupons/bulk`,
        {
          prefix: bulk.prefix.trim().toUpperCase(),
          count: Number(bulk.count),
          percent_off: Number(bulk.percent_off),
          valid_days: Number(bulk.valid_days),
          max_uses: Number(bulk.max_uses),
          package_id: bulk.package_id || null,
          description: bulk.description || null,
        },
        { headers: authHeaders() }
      );

      const codes = response.data.codes || [];

      setGeneratedCodes(codes);

      toast.success(`Wygenerowano ${codes.length} kodów`);

      if (onRefresh) {
        await onRefresh();
      }
    } catch (error) {
      toast.error(
        error.response?.data?.detail ||
          "Nie udało się wygenerować kodów"
      );
    } finally {
      setBulkLoading(false);
    }
  };

  const copyCodes = async () => {
    if (!generatedCodes.length) {
      return;
    }

    try {
      await navigator.clipboard.writeText(
        generatedCodes.join("\n")
      );

      toast.success("Kody skopiowano do schowka");
    } catch {
      toast.error("Nie udało się skopiować kodów");
    }
  };

  return (
    <div className="mt-8 grid grid-cols-1 gap-6 xl:grid-cols-2">
      <div className="dc-card p-6">
        <div className="mb-5 flex items-center gap-3">
          <div className="rounded-2xl bg-[#ff4458]/10 p-3 text-[#ff4458]">
            <Mail className="h-5 w-5" />
          </div>

          <div>
            <h2 className="font-display text-xl font-bold text-white">
              Newsletter
            </h2>

            <p className="mt-1 text-xs text-[#a8adbd]">
              Testuj albo uruchom automatyczną wysyłkę.
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-[#a8adbd]">
              Adres testowy
            </label>

            <div className="flex flex-col gap-3 sm:flex-row">
              <input
                type="email"
                value={testEmail}
                onChange={(event) =>
                  setTestEmail(event.target.value)
                }
                placeholder="kontakt@mateuszzalewski.pl"
                className="min-w-0 flex-1 rounded-xl border border-[#2a2f3d] bg-[#0f1117] px-4 py-3 text-white outline-none focus:border-[#ff4458]"
              />

              <button
                onClick={sendTestEmail}
                disabled={testLoading}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-[#303545] bg-[#171a22] px-5 py-3 text-sm font-semibold text-white hover:border-[#ff4458]/50 disabled:opacity-50"
              >
                {testLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                Wyślij test
              </button>
            </div>
          </div>

          <div className="rounded-2xl border border-[#fdb022]/20 bg-[#fdb022]/5 p-4">
            <div className="text-sm font-semibold text-[#fdb022]">
              Uwaga
            </div>

            <p className="mt-1 text-xs leading-relaxed text-[#a8adbd]">
              Przycisk poniżej uruchamia istniejący system
              newslettera dla zapisanych odbiorców. Najpierw wyślij
              wiadomość testową.
            </p>
          </div>

          <button
            onClick={sendNewsletterNow}
            disabled={newsletterLoading}
            className="inline-flex w-full items-center justify-center gap-2 rounded-full dc-gradient-bg px-5 py-3 font-display text-sm font-semibold text-white disabled:opacity-50"
          >
            {newsletterLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Play className="h-4 w-4" />
            )}
            Wyślij newsletter teraz
          </button>
        </div>
      </div>

      <div className="dc-card p-6">
        <div className="mb-5 flex items-center gap-3">
          <div className="rounded-2xl bg-[#fdb022]/10 p-3 text-[#fdb022]">
            <Gift className="h-5 w-5" />
          </div>

          <div>
            <h2 className="font-display text-xl font-bold text-white">
              Masowy generator kodów
            </h2>

            <p className="mt-1 text-xs text-[#a8adbd]">
              Wygeneruj od 1 do 1000 unikalnych kodów.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <input
            value={bulk.prefix}
            onChange={(event) =>
              setBulk((current) => ({
                ...current,
                prefix: event.target.value.toUpperCase(),
              }))
            }
            placeholder="Prefiks, np. SUMMER"
            className="rounded-xl border border-[#2a2f3d] bg-[#0f1117] px-4 py-3 text-white outline-none focus:border-[#ff4458]"
          />

          <input
            type="number"
            min="1"
            max="1000"
            value={bulk.count}
            onChange={(event) =>
              setBulk((current) => ({
                ...current,
                count: event.target.value,
              }))
            }
            placeholder="Liczba kodów"
            className="rounded-xl border border-[#2a2f3d] bg-[#0f1117] px-4 py-3 text-white outline-none focus:border-[#ff4458]"
          />

          <input
            type="number"
            min="1"
            max="90"
            value={bulk.percent_off}
            onChange={(event) =>
              setBulk((current) => ({
                ...current,
                percent_off: event.target.value,
              }))
            }
            placeholder="Rabat %"
            className="rounded-xl border border-[#2a2f3d] bg-[#0f1117] px-4 py-3 text-white outline-none focus:border-[#ff4458]"
          />

          <input
            type="number"
            min="1"
            max="365"
            value={bulk.valid_days}
            onChange={(event) =>
              setBulk((current) => ({
                ...current,
                valid_days: event.target.value,
              }))
            }
            placeholder="Ważność w dniach"
            className="rounded-xl border border-[#2a2f3d] bg-[#0f1117] px-4 py-3 text-white outline-none focus:border-[#ff4458]"
          />

          <input
            type="number"
            min="1"
            value={bulk.max_uses}
            onChange={(event) =>
              setBulk((current) => ({
                ...current,
                max_uses: event.target.value,
              }))
            }
            placeholder="Limit użyć na kod"
            className="rounded-xl border border-[#2a2f3d] bg-[#0f1117] px-4 py-3 text-white outline-none focus:border-[#ff4458]"
          />

          <select
            value={bulk.package_id}
            onChange={(event) =>
              setBulk((current) => ({
                ...current,
                package_id: event.target.value,
              }))
            }
            className="rounded-xl border border-[#2a2f3d] bg-[#0f1117] px-4 py-3 text-white outline-none focus:border-[#ff4458]"
          >
            <option value="">Wszystkie pakiety</option>
            <option value="single">1 kredyt</option>
            <option value="pack10">10 kredytów</option>
            <option value="monthly">
              15 kredytów miesięcznie
            </option>
          </select>
        </div>

        <textarea
          value={bulk.description}
          onChange={(event) =>
            setBulk((current) => ({
              ...current,
              description: event.target.value,
            }))
          }
          rows={2}
          placeholder="Opis partii kodów"
          className="mt-3 w-full rounded-xl border border-[#2a2f3d] bg-[#0f1117] px-4 py-3 text-white outline-none focus:border-[#ff4458]"
        />

        <button
          onClick={generateBulkCoupons}
          disabled={bulkLoading}
          className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#fdb022] px-5 py-3 font-display text-sm font-bold text-[#111318] disabled:opacity-50"
        >
          {bulkLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Gift className="h-4 w-4" />
          )}
          Generuj kody
        </button>

        {generatedCodes.length > 0 && (
          <div className="mt-5 rounded-2xl border border-[#32d583]/20 bg-[#32d583]/5 p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-[#32d583]">
                  Wygenerowano: {generatedCodes.length}
                </div>

                <div className="mt-1 text-xs text-[#a8adbd]">
                  Pierwsze kody:
                </div>
              </div>

              <button
                onClick={copyCodes}
                className="inline-flex items-center gap-2 rounded-full border border-[#32d583]/30 px-3 py-2 text-xs font-semibold text-[#32d583]"
              >
                <Copy className="h-3.5 w-3.5" />
                Kopiuj wszystkie
              </button>
            </div>

            <div className="mt-3 max-h-36 overflow-auto rounded-xl bg-[#0f1117] p-3 font-mono text-xs text-white">
              {generatedCodes.slice(0, 30).map((code) => (
                <div key={code}>{code}</div>
              ))}

              {generatedCodes.length > 30 && (
                <div className="mt-2 text-[#6f7484]">
                  …oraz {generatedCodes.length - 30} kolejnych.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
